import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { money } from "@/lib/api";

export default function CartPage() {
  const { items, updateQuantity, removeItem, subtotal } = useCart();
  const navigate = useNavigate();

  if (items.length === 0) {
    return (
      <div data-testid="cart-empty" className="jm-container py-24 text-center">
        <ShoppingBag className="w-12 h-12 text-navy/30 mx-auto" />
        <h1 className="font-display text-4xl text-navy mt-4">Your cart is empty</h1>
        <p className="text-ink/60 mt-2">Go find some treasures worth keeping.</p>
        <Link to="/shop" className="mt-6 inline-block bg-brick text-white font-bold uppercase rounded-md px-6 py-3.5 hover:bg-navy transition-colors">
          Browse the Shop
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="jm-container py-12">
      <h1 className="font-display text-4xl sm:text-5xl text-navy">Your Cart</h1>

      <div className="mt-8 grid gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-4">
          {items.map((item) => (
            <div
              key={item.product_id}
              data-testid={`cart-item-${item.product_id}`}
              className="flex gap-4 bg-white rounded-lg border border-navy/15 p-4"
            >
              <div className="w-24 h-24 rounded-md overflow-hidden bg-cream shrink-0">
                {item.image && <img src={item.image} alt={item.name} className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-display text-lg text-navy leading-tight">{item.name}</h3>
                {item.variant_label && (
                  <p data-testid={`cart-variant-${item.product_id}`} className="text-xs font-semibold text-navy/60 mt-0.5">Option: {item.variant_label}</p>
                )}
                <p className="text-brick font-bold mt-1">{money(item.price)}</p>
                <div className="mt-3 flex items-center gap-3">
                  <div className="flex items-center border border-navy/25 rounded-md">
                    <button data-testid={`cart-dec-${item.product_id}`} onClick={() => updateQuantity(item.product_id, item.quantity - 1, item.variant_vid)} className="px-2.5 py-2 text-navy hover:bg-cream">
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="px-3 text-sm font-bold text-navy">{item.quantity}</span>
                    <button data-testid={`cart-inc-${item.product_id}`} onClick={() => updateQuantity(item.product_id, item.quantity + 1, item.variant_vid)} disabled={item.max_qty != null && item.quantity >= item.max_qty} className="px-2.5 py-2 text-navy hover:bg-cream disabled:opacity-40 disabled:cursor-not-allowed">
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <button data-testid={`cart-remove-${item.product_id}`} onClick={() => removeItem(item.product_id, item.variant_vid)} className="text-navy/50 hover:text-brick inline-flex items-center gap-1 text-sm">
                    <Trash2 className="w-4 h-4" /> Remove
                  </button>
                </div>
              </div>
              <div className="font-display text-lg text-navy self-center whitespace-nowrap">
                {money(item.price * item.quantity)}
              </div>
            </div>
          ))}
        </div>

        <div className="bg-cream border border-navy/15 rounded-lg p-6 h-fit">
          <h2 className="font-display text-2xl text-navy">Order Summary</h2>
          <div className="mt-4 flex justify-between text-ink/80">
            <span>Subtotal</span>
            <span data-testid="cart-subtotal" className="font-bold">{money(subtotal)}</span>
          </div>
          <p className="text-sm text-ink/50 mt-1">Shipping calculated at checkout.</p>
          <button
            data-testid="checkout-btn"
            onClick={() => navigate("/checkout")}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-brick text-white font-bold uppercase rounded-md px-6 py-3.5 hover:bg-navy transition-colors"
          >
            Checkout <ArrowRight className="w-4 h-4" />
          </button>
          <Link to="/shop" className="mt-3 block text-center text-navy/70 hover:text-brick font-semibold text-sm">
            Continue shopping
          </Link>
        </div>
      </div>
    </div>
  );
}
